<template>
  <a-card
    title="Теги"
    size="small">
    <template #extra>
      <a-space>
        <a-button
          v-if="isDirty"
          size="small"
          @click="reset">
          Отменить
        </a-button>
        <a-button
          type="primary"
          size="small"
          :disabled="!isDirty"
          :loading="isSaving"
          @click="save">
          Сохранить
        </a-button>
      </a-space>
    </template>

    <a-empty
      v-if="!tags.length"
      description="Справочник тегов пуст" />

    <a-select
      v-else
      v-model:value="selectedIds"
      mode="multiple"
      placeholder="Выберите теги"
      :show-search="true"
      option-filter-prop="label"
      :style="{ width: '100%' }">
      <a-select-opt-group
        v-for="group in groupedOptions"
        :key="group.label"
        :label="group.label">
        <a-select-option
          v-for="option in group.options"
          :key="option.value"
          :value="option.value"
          :label="option.label">
          {{ option.label }}
        </a-select-option>
      </a-select-opt-group>
    </a-select>
  </a-card>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { message } from 'ant-design-vue';
import { showApiError } from '@/helpers/apiError';
import { getRuEnLabel } from '@/helpers/tagLabels';

const props = defineProps({
  /** Неймспейс vuex-модуля сущности. */
  storeNamespace: { type: String, required: true },
  /** Действие сохранения: получает { id, payload: { tag_ids } }. */
  updateAction: { type: String, required: true },
  entityId: { type: [Number, String], required: true },
  /** Теги, уже назначенные элементу. */
  assignedTags: { type: Array, default: () => [] },
  /** Весь справочник тегов сущности. */
  tags: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved']);

const store = useStore();

const isSaving = ref(false);
const selectedIds = ref([]);

const groupedOptions = computed(() => {
  const groups = new Map();

  props.tags.forEach((tag) => {
    const label = getRuEnLabel(tag.ru_category, tag.en_category);

    if (!groups.has(label)) {
      groups.set(label, { label, options: [] });
    }

    groups.get(label).options.push({ value: Number(tag.id), label: getRuEnLabel(tag.ru, tag.en) });
  });

  return Array.from(groups.values());
});

const assignedIds = computed(() => props.assignedTags.map((tag) => Number(tag.id)).sort((a, b) => a - b));

const isDirty = computed(() => {
  const current = [...selectedIds.value].map(Number).sort((a, b) => a - b);

  return JSON.stringify(current) !== JSON.stringify(assignedIds.value);
});

const reset = () => {
  selectedIds.value = [...assignedIds.value];
};

watch(assignedIds, reset, { immediate: true });

const save = async () => {
  isSaving.value = true;

  try {
    await store.dispatch(`${props.storeNamespace}/${props.updateAction}`, {
      id: Number(props.entityId),
      payload: { tag_ids: selectedIds.value.map(Number) },
    });

    message.success('Теги сохранены');
    emit('saved');
  } catch (error) {
    showApiError(error, 'Не удалось сохранить теги');
  } finally {
    isSaving.value = false;
  }
};
</script>
